/**
 * AI-made Block of Exercises — prompts, presets, parsing.
 */
import { EXERCISE_TYPES } from '../components/ExerciseTypeSelect';
import { getExerciseSyntaxHint } from './exerciseEditSyntaxHints.js';
import { mapLevelToCefr } from './magicFillWords.js';

export const AI_BLOCK_PRESETS_SETTING_KEY = 'ai_block_exercise_presets';

/** Skills offered in the Create-with-AI overlay (subset of classroom focus). */
export const AI_BLOCK_SKILL_OPTIONS = [
    'Reading',
    'Vocabulary',
    'Grammar',
    'Functional',
    'Listening',
    'Speaking',
];

export const AI_BLOCK_VALID_TYPE_KEYS = EXERCISE_TYPES.map((t) => t.key);

/** Normalize one list slot: string → { type, notes } (legacy presets supported). */
export function normalizeAiBlockTypeSlot(raw) {
    if (typeof raw === 'string') {
        const type = raw.trim();
        if (!AI_BLOCK_VALID_TYPE_KEYS.includes(type)) return null;
        return { type, notes: '' };
    }
    if (!raw || typeof raw !== 'object') return null;
    const type = String(raw.type || raw.key || '').trim();
    if (!AI_BLOCK_VALID_TYPE_KEYS.includes(type)) return null;
    return { type, notes: String(raw.notes || '').trim() };
}

export function normalizeAiBlockTypeList(rawList) {
    if (!Array.isArray(rawList)) return [];
    return rawList.map(normalizeAiBlockTypeSlot).filter(Boolean);
}

/** Map AI-suggested type keys onto slots, preserving notes from prior slots when types match. */
export function mergeSuggestedTypeSlots(suggestedKeys, previousSlots = []) {
    const prev = normalizeAiBlockTypeList(previousSlots);
    const used = new Set();
    return (suggestedKeys || [])
        .map((key) => String(key || '').trim())
        .filter((key) => AI_BLOCK_VALID_TYPE_KEYS.includes(key))
        .map((type) => {
            const matchIdx = prev.findIndex((s, i) => s.type === type && !used.has(i));
            if (matchIdx >= 0) {
                used.add(matchIdx);
                return { type, notes: prev[matchIdx].notes || '' };
            }
            return { type, notes: '' };
        });
}

export function normalizeAiBlockPresets(raw) {
    const list = Array.isArray(raw?.presets) ? raw.presets : (Array.isArray(raw) ? raw : []);
    return {
        presets: list
            .map((p) => ({
                id: String(p?.id || `preset-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`),
                name: String(p?.name || '').trim() || 'Untitled preset',
                types: normalizeAiBlockTypeList(p?.types),
            }))
            .filter((p) => p.types.length > 0 || p.name),
    };
}

export function extractJsonPayload(raw) {
    const text = String(raw || '').trim();
    if (!text) return null;
    const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
    const candidate = (fenced ? fenced[1] : text).trim();
    const startObj = candidate.indexOf('{');
    const startArr = candidate.indexOf('[');
    let start = -1;
    let end = -1;
    if (startObj >= 0 && (startArr < 0 || startObj < startArr)) {
        start = startObj;
        end = candidate.lastIndexOf('}');
    } else if (startArr >= 0) {
        start = startArr;
        end = candidate.lastIndexOf(']');
    }
    if (start < 0 || end <= start) {
        try {
            return JSON.parse(candidate);
        } catch {
            return null;
        }
    }
    try {
        return JSON.parse(candidate.slice(start, end + 1));
    } catch {
        return null;
    }
}

export function parseSuggestedTypes(raw) {
    const parsed = extractJsonPayload(raw);
    let types = [];
    if (Array.isArray(parsed)) {
        types = parsed;
    } else if (parsed && Array.isArray(parsed.types)) {
        types = parsed.types;
    }
    return [...new Set(
        types
            .map((t) => (typeof t === 'string' ? t : t?.type || t?.key || ''))
            .map((t) => String(t).trim())
            .filter((t) => AI_BLOCK_VALID_TYPE_KEYS.includes(t)),
    )];
}

export function parseGeneratedExercise(raw, expectedType) {
    const parsed = extractJsonPayload(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('AI returned invalid exercise JSON');
    }
    const type = AI_BLOCK_VALID_TYPE_KEYS.includes(parsed.type)
        ? parsed.type
        : expectedType;
    return {
        type: type || expectedType,
        title: String(parsed.title || '').trim() || `New ${expectedType}`,
        instruction: String(parsed.instruction || '').trim(),
        body: String(parsed.body ?? parsed.content ?? '').trim(),
    };
}

export function buildAiBlockContext({
    language = 'en',
    course = '',
    skill = '',
    inputText = '',
    instructions = '',
} = {}) {
    const cefr = mapLevelToCefr(course) || '';
    return {
        language: String(language || 'en').trim() || 'en',
        course: String(course || '').trim(),
        cefr,
        skill: String(skill || '').trim(),
        inputText: String(inputText || '').trim(),
        instructions: String(instructions || '').trim(),
    };
}

export const SUGGEST_BLOCK_TYPES_SYSTEM_PROMPT = `You are a curriculum designer for an English-learning classroom platform.
Given teacher input (topic/words/text), CEFR level, and skill focus, suggest a short sequence of exercise TYPES that fit a practice block.

Rules:
1. Return ONLY valid JSON: {"types":["type_key",...]} — no markdown fences, no commentary.
2. Use ONLY type keys from the allowed list provided in the user message.
3. Prefer 3–7 exercises unless the teacher asks otherwise.
4. Order from warmer/easier → deeper practice when possible.
5. Match the skill focus (e.g. Vocabulary → wordlist / gaps / matching; Grammar → gaps / error_correction / MC; Reading → text / MC / comprehension-style text tasks; Listening → dictation / gaps; Speaking → voice_record / essay prompts / discussion-style text).
6. Do not invent type keys.`;

export function buildSuggestBlockTypesUserPrompt(ctx, {
    existingTypes = [],
    emptyList = false,
} = {}) {
    const allowed = AI_BLOCK_VALID_TYPE_KEYS.join(', ');
    const existingSlots = normalizeAiBlockTypeList(existingTypes);
    const existingKeys = existingSlots.map((s) => s.type);
    const existingLines = existingSlots.length
        ? existingSlots.map((s, i) => `${i + 1}. ${s.type}${s.notes ? ` — notes: ${s.notes}` : ''}`).join('\n')
        : '(none)';
    return `Allowed type keys:
${allowed}

Context:
- Language: ${ctx.language || 'en'}
- Course: ${ctx.course || '(none)'}
- CEFR: ${ctx.cefr || '(infer from course or default B1)'}
- Skill: ${ctx.skill || '(general)'}
- Teacher input / words / source:
${ctx.inputText || '(none)'}
- Extra instructions:
${ctx.instructions || '(none)'}

${emptyList
        ? 'The teacher has NOT selected any exercise types yet. Propose a full practice sequence as "types".'
        : `The teacher already selected these types (keep them in order, then ADD complementary types if useful):\n${existingLines}\nReturn the FULL proposed sequence in "types" (existing first, then suggestions). Existing keys: ${existingKeys.join(', ') || '(none)'}`}

Respond with JSON only: {"types":["..."]}`;
}

export const GENERATE_BLOCK_EXERCISE_SYSTEM_PROMPT = `You create a single classroom exercise for an English-learning platform.
Return ONLY valid JSON with keys: type, title, instruction, body.
No markdown fences, no commentary outside JSON.

Rules:
1. "type" must equal the requested type key.
2. "title" — short teacher-facing title.
3. "instruction" — student-facing task (1–3 sentences). Can be empty only if the body is self-explanatory.
4. "body" — exercise content using the EXACT marker syntax for that type (see coding rules in the user message).
5. Match CEFR language difficulty. Prefer the teacher’s source words/text; do not invent unrelated topics.
6. For image/audio-heavy types (illustration, image_*, dictation, voice_*, powerpoint_presentation, slides with URLs): still produce valid text scaffolding; use placeholder URLs only if the syntax requires them (https://example.com/...).
7. LENGTH — practice items must feel substantial, not tiny warm-ups:
   - Gap / choice / error types (fill_gaps*, fill_gaps_choice, multiple_choice_*, error_correction, checklist, jumbled_letters, order_words, order_lines, matching_columns, sort_boxes, image_fill_gaps, image_matching, sentence_slides, wordlist, wordlist_slides, dictation, voice_compare): include **10–15** scored gaps, items, pairs, lines, or words (aim for ~12). Never fewer than 10 unless the teacher’s Extra instructions or this exercise’s Notes explicitly ask for fewer.
   - Reading / text / essay / voice_record / illustration / text_slides / disappearing_text: use enough content for a real classroom task (a full short paragraph or several slides), not a 1–2 sentence stub.
8. Do not pad with empty lines; every item must be a real practice item.
9. If "Exercise notes" are provided for this slot, treat them as the primary brief for what this exercise must include (focus, structures, word set, task shape). Follow them closely.`;

export function buildGenerateBlockExerciseUserPrompt(ctx, exerciseType, exerciseNotes = '') {
    const label = EXERCISE_TYPES.find((t) => t.key === exerciseType)?.label || exerciseType;
    const syntax = getExerciseSyntaxHint(exerciseType);
    const notes = String(exerciseNotes || '').trim();
    return `Create one exercise.

Type key: ${exerciseType}
Type label: ${label}

Coding rules for body:
${syntax}

LENGTH REQUIREMENT: Prefer **10–15** practice gaps/items/pairs/lines/words in the body (about 12). Do not produce a short 4–6 item exercise unless Extra instructions or Exercise notes explicitly request a shorter set.

Context:
- Language: ${ctx.language || 'en'}
- Course: ${ctx.course || '(none)'}
- CEFR: ${ctx.cefr || '(default B1)'}
- Skill: ${ctx.skill || '(general)'}
- Teacher input / words / source:
${ctx.inputText || '(none — invent a coherent topic at the CEFR level with enough material for 10–15 items)'}
- Extra instructions (whole block):
${ctx.instructions || '(none)'}
- Exercise notes (this exercise only — follow closely):
${notes || '(none)'}

Respond with JSON only:
{"type":"${exerciseType}","title":"...","instruction":"...","body":"..."}`;
}
